import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Hook to read the hash and navigate to correct tab from provided list.
 * Returns array of a string (tab value), function (navigation function), and function (reactive tab function).
 * @param {Array<{value: string, displayName: string}>} tabs
 * @returns {[string, (newTab: string, replace?: boolean) => void, (newTab: string) => void, (newTab: string) => void]}
 */
const useUriFragment = tabs => {
  const location = useLocation();
  const { hash, pathname } = location;

  const [activeTab, setActiveTab] = useState(tabs.length > 0 ? tabs[0].value : '');
  const navigate = useNavigate();

  const navigateToTab = useCallback(
    (newTab, replace = false) => {
      navigate(`${pathname}#${newTab}`, { replace });
    },
    [navigate, pathname]
  );

  useEffect(() => {
    if (!tabs.length || activeTab) {
      return;
    }

    navigateToTab(tabs[0].value);
  }, [tabs, activeTab, navigateToTab]);

  useEffect(() => {
    if (!hash) {
      navigate(`${pathname}#${tabs.length > 0 ? tabs[0].value : ''}`, {
        state: location.state,
        replace: true
      });
    } else {
      setActiveTab(hash.substring(1));
    }
  }, [hash, pathname, tabs, location.state, navigate]);

  const reactiveTab = useCallback(
    newTab => {
      navigate(`${pathname}#${newTab}`, { replace: true });
    },
    [navigate, pathname]
  );

  const navigateToTabWithLocationState = newTab => {
    navigate(`${pathname}#${newTab}`, { replace: true, state: location.state });
  };

  return [activeTab, navigateToTab, reactiveTab, navigateToTabWithLocationState];
};

export default useUriFragment;
